import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, doc, setDoc, deleteDoc, onSnapshot, query } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

interface HomepageContent {
  id: string;
  type: 'video' | 'image';
  url: string;
  position: number;
  title?: string;
  description?: string;
}

const HomepageManagement = () => {
  const [contents, setContents] = useState<HomepageContent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<Partial<HomepageContent> & { file?: File }>({ 
    type: 'image', 
    position: 1 
  });
  const [isEditing, setIsEditing] = useState(false);

  // Fetch homepage contents (Firestore)
  useEffect(() => {
    const q = query(collection(db, 'homepageContents'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items: HomepageContent[] = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<HomepageContent, 'id'>),
      }));
      setContents(items);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'position' ? parseInt(value) : value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        file: e.target.files?.[0]
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.type) return;

    try {
      setIsLoading(true);
      let contentData: Omit<HomepageContent, 'id'> = {
        type: formData.type as 'video' | 'image',
        url: formData.url || '',
        position: formData.position || 1,
        title: formData.title,
        description: formData.description
      };

      // Handle file upload to Cloudinary if it's an image
      if (formData.type === 'image' && formData.file) {
        // Cloudinary credentials (reuse from ResourceManagement)
        const cloudName = 'dhxiqrn0d';
        const uploadPreset = 'marazsir';
        const url = `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`;
        const fd = new FormData();
        fd.append('file', formData.file);
        fd.append('upload_preset', uploadPreset);

        const res = await fetch(url, { method: 'POST', body: fd });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`Cloudinary upload failed (${res.status}): ${txt}`);
        }
        const data = await res.json();
        contentData.url = data.secure_url;
      }

      // Save to Firestore
      const contentId = isEditing && formData.id ? formData.id : uuidv4();
      await setDoc(doc(db, 'homepageContents', contentId), contentData);

      // Reset form
      setFormData({ type: 'image', position: 1 });
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Error saving content. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (content: HomepageContent) => {
    setFormData({
      id: content.id,
      type: content.type,
      url: content.url,
      position: content.position,
      title: content.title,
      description: content.description
    });
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (content: HomepageContent) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;

    try {
      setIsLoading(true);
      // Delete from Firestore
      await deleteDoc(doc(db, 'homepageContents', content.id));
      // Note: Deleting assets from Cloudinary requires server-side credentials.
      // Please delete from Cloudinary dashboard or implement a secure backend endpoint.
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Error deleting content. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const cancelEdit = () => {
    setFormData({ type: 'image', position: 1 });
    setIsEditing(false);
  };

  if (isLoading && contents.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-2xl font-bold mb-6">
          {isEditing ? 'Edit Homepage Content' : 'Add New Homepage Content'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content Type
              </label>
              <select
                name="type"
                value={formData.type || ''}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="image">Image</option>
                <option value="video">Video (URL)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position
              </label>
              <input
                type="number"
                name="position"
                min="1"
                value={formData.position || ''}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title (Optional)
            </label>
            <input
              type="text"
              name="title"
              value={formData.title || ''}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter a title for this content"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              name="description"
              value={formData.description || ''}
              onChange={handleInputChange}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter a description for this content"
            />
          </div>

          {formData.type === 'video' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Video URL
              </label>
              <input
                type="url"
                name="url"
                value={formData.url || ''}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://www.youtube.com/watch?v=..."
                required={!isEditing || formData.type === 'video'}
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required={!isEditing || !formData.url}
              />
              {formData.url && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">Current image:</p>
                  <img 
                    src={formData.url} 
                    alt="Preview" 
                    className="mt-1 h-32 object-cover rounded"
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            {isEditing && (
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : isEditing ? 'Update Content' : 'Add Content'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Current Homepage Contents</h2>
        </div>
        
        {contents.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No homepage content added yet. Start by adding content above.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {contents
              .sort((a, b) => a.position - b.position)
              .map((content) => (
                <li key={content.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {content.type === 'image' ? (
                        <img
                          src={content.url}
                          alt={content.title || 'Homepage content'}
                          className="h-16 w-24 object-cover rounded"
                        />
                      ) : (
                        <div className="h-16 w-24 bg-gray-200 rounded flex items-center justify-center">
                          <span className="text-gray-400">Video</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {content.title || `Content ${content.position}`}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        Type: {content.type} | Position: {content.position}
                      </p>
                      {content.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {content.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(content)}
                        className="p-2 text-blue-600 hover:text-blue-800 focus:outline-none"
                        title="Edit"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(content)}
                        className="p-2 text-red-600 hover:text-red-800 focus:outline-none"
                        title="Delete"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default HomepageManagement;
